import {
  NumberRoundingMode,
  numberFormatter,
} from '../../../datatypes/numberFormatter'

/**
 * Per-chart value formatting passed through `ChartData.valueFormat` (and configurable
 * from view configs via `ChartWidget`'s `valueFormat` prop).
 *
 * Formatting behavior is delegated to {@link numberFormatter}; treat its docs as the
 * single source of truth for precision, rounding modes, percent handling, shorthand,
 * and numeric-string parsing.
 */
export type ChartValueFormat = {
  precision?: number
  locale?: string
  useShorthand?: boolean
  style?: 'decimal' | 'percent'
  nullDisplay?: string
  roundingMode?: NumberRoundingMode
  /** Text appended after the formatted number (e.g. `" days"`). Percent style already adds `%`. */
  suffix?: string
  /** Text prepended before the formatted number (e.g. `"$"`). */
  prefix?: string
}

/** Default chart precision when `valueFormat.precision` is omitted. */
const DEFAULT_CHART_VALUE_PRECISION = 1

/** Format a chart numeric value (labels, tooltips, pie totals). */
export const formatChartDataLabelValue = (
  value: unknown,
  format?: ChartValueFormat,
): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return String(value ?? '')
  }

  const { prefix = '', suffix = '', ...rest } = format ?? {}
  const numberOptions = {
    numberPrecision: rest.precision ?? DEFAULT_CHART_VALUE_PRECISION,
    numberLocale: rest.locale,
    numberFormat: rest.useShorthand ? 'abbreviated' : 'standard',
    numberUnit: rest.style === 'percent' ? 'percentage' : 'number',
    numberNullDisplay: rest.nullDisplay,
    numberRoundingMode: rest.roundingMode,
  } as const
  const body = numberFormatter(value, numberOptions)
  return `${prefix}${body}${suffix}`
}
